import { DoubleRightOutlined } from "@ant-design/icons"
import { Tag, Tooltip, Button, Table } from "antd"
import { ColumnsType } from "antd/es/table"
import { FC, useState, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import { EResultTagAdequacy } from "../../../components/commons-ts/common"
import { Equipment, Impact, UUID, Zone } from "../../../generated/proto-ts/main"
import { Translated } from "../../../utils/translated"
import { pbUUIDToUuid, COLOR_BG_ROW_DELETED, formatDayOrLongDash } from "../../../utils/utils"
import { getEquipmentResultSM } from "../../../calculus/calculus_SM"
import { IEquipmentResult } from "../../../calculus/types"
import { TableImageThumbnail } from "../../../components/commons-ts/tableImageThumbnail"
import { useUsercommSiteChildrenRecursiveBimodal } from "../../../usercomm/common/usercommAsyncRequestBimodal"
import { useSyncronizationContext } from "../../../providers/syncronizationProvider"
import dayjs from "dayjs"

interface IEquipmentProcessedSM {
  equipment: Equipment
  nb_zones: number
  nb_impacts: number
  result: IEquipmentResult | null
}

export const SiteEquipmentsTableSM: FC<{
  siteUUID: UUID | null
  equipments: Equipment[] | null
}> = ({ siteUUID, equipments }) => {
  const [equipmentResultsMap, setEquipmentResultsMap] = useState<Record<
    string,
    IEquipmentResult
  > | null>(null)
  const [_, equipmentZonesMap, zoneImpactsMap, getEntitiesRecursive] =
    useUsercommSiteChildrenRecursiveBimodal()
  const { isAdmin } = useSyncronizationContext()

  useEffect(() => {
    if (siteUUID === null) {
      return
    }
    getEntitiesRecursive(siteUUID)
  }, [siteUUID, getEntitiesRecursive])

  useEffect(() => {
    if (equipments === null || equipmentZonesMap === null || zoneImpactsMap === null) {
      return
    }
    let _equipmentResultsMap: Record<string, IEquipmentResult> = {}
    for (let equipment of equipments) {
      let equipmentUUIDStr = pbUUIDToUuid(equipment.uuid)
      let equipmentZones = equipmentZonesMap[equipmentUUIDStr]
      if (equipmentZones === undefined) {
        continue
      }
      let result = getEquipmentResultSM(equipment, equipmentZones, zoneImpactsMap)
      if (result === null) {
        continue
      }
      _equipmentResultsMap[equipmentUUIDStr] = result
    }
    setEquipmentResultsMap(_equipmentResultsMap)
  }, [equipmentZonesMap, zoneImpactsMap])

  const columns = useMemo((): ColumnsType<Equipment> => {
    return [
      {
        title: "📷", // Photo
        render: (_, equipment) => {
          let pictureUUID = equipment.pictures[0]
          if (pictureUUID === undefined) {
            return <div style={{ width: "3rem" }} />
          }
          return (
            <Link to={`/equipments/${pbUUIDToUuid(equipment.uuid)}`}>
              <TableImageThumbnail pictureUUID={pictureUUID} alt={equipment.equipment_name} />
            </Link>
          )
        },
      },
      {
        key: "equipment",
        title: <Translated keyEn="Equipment" />,
        ellipsis: true,
        render: (_, equipment) => {
          return (
            <Link
              to={`/equipments/${pbUUIDToUuid(equipment.uuid)}`}
              style={{
                textDecoration: "none",
              }}
            >
              {equipment.equipment_name}
            </Link>
          )
        },
      },
      {
        title: <Translated keyEn="Mat type" />,
        render: (_, equipment) => {
          if (equipment.sports_mat_type === null) {
            return <span>N/A</span>
          }
          return <span>Type {equipment.sports_mat_type}</span>
        },
      },
      {
        title: <Translated keyEn="# zones" />,
        render: (_, equipment) => {
          if (equipmentZonesMap === null) {
            return null
          }

          let equipmentUUIDStr = pbUUIDToUuid(equipment.uuid)
          let equipmentZones = equipmentZonesMap[equipmentUUIDStr]
          if (equipmentZones == undefined) {
            return <Tag>N/A</Tag>
          }
          let nbZones = 0
          for (let zone of equipmentZones) {
            if (zone.deleted_at === 0) {
              nbZones++
            }
          }
          return <span>{nbZones}</span>
        },
      },
      {
        title: <Translated keyEn="# Impacts" />,
        render: (_, equipment) => {
          if (equipmentZonesMap === null || zoneImpactsMap === null) {
            return null
          }
          let equipmentUUIDStr = pbUUIDToUuid(equipment.uuid)
          let equipmentZones = equipmentZonesMap[equipmentUUIDStr]
          if (equipmentZones == undefined) {
            return <Tag>N/A</Tag>
          }
          let nbImpacts = 0
          for (let zone of equipmentZones) {
            if (zone.deleted_at === 0) {
              let zoneImpacts = zoneImpactsMap[pbUUIDToUuid(zone.uuid)]
              if (zoneImpacts === undefined) {
                continue
              }
              for (let impact of zoneImpacts) {
                if (impact.deleted_at === 0) {
                  nbImpacts++
                }
              }
            }
          }
          return <span>{nbImpacts}</span>
        },
      },
      {
        key: "result",
        title: (
          <Tooltip overlay={<Translated keyEn="Result" />}>
            <Translated keyEn="Result" />
          </Tooltip>
        ),
        render: (_, equipment) => {
          if (equipmentResultsMap === null) {
            return null
          }
          let equipmentUUIDStr = pbUUIDToUuid(equipment.uuid)
          let result = equipmentResultsMap[equipmentUUIDStr]
          if (result === undefined || result === null) {
            return <Tag>N/A</Tag>
          }
          return <EResultTagAdequacy result={result.result} />
        },
      },
      {
        title: "",
        width: "2rem",
        render: (_, equipment) => {
          return (
            <Link to={`/equipments/${pbUUIDToUuid(equipment.uuid)}`}>
              <Button type="link" size="small">
                <DoubleRightOutlined />
              </Button>
            </Link>
          )
        },
      },
    ]
  }, [equipments, equipmentZonesMap, zoneImpactsMap, equipmentResultsMap])

  return (
    <Table
      dataSource={equipments ?? []}
      loading={equipmentResultsMap === null}
      columns={columns}
      pagination={false}
      rowKey={(e) => pbUUIDToUuid(e.uuid)}
      size="small"
      scroll={{ x: true }}
      bordered
      onRow={(record) => {
        if (record.deleted_at === 0) {
          return {}
        }
        if (isAdmin) {
          return {
            style: {
              backgroundColor: COLOR_BG_ROW_DELETED,
            },
          }
        } else {
          return {
            style: {
              display: "none",
            },
          }
        }
      }}
      footer={() => <Translated keyEn="Site Equipments Table for EN 12503 - SPORTS MATS" />}
    />
  )
}

export const SiteEquipmentsTableMetadataSM_Print: FC<{
  siteUUID: UUID | null
  equipments: Equipment[] | null
  equipmentZonesMap: Record<string, Zone[]> | null
  zoneImpactsMap: Record<string, Impact[]> | null
}> = ({ equipments, equipmentZonesMap, zoneImpactsMap }) => {
  const [equipmentsProcessed, setEquipmentsProcessed] = useState<IEquipmentProcessedSM[] | null>(
    null,
  )

  useEffect(() => {
    if (equipments === null || equipmentZonesMap === null || zoneImpactsMap === null) {
      return
    }
    let _equipmentsProcessed: IEquipmentProcessedSM[] = []
    for (let equipment of equipments) {
      if (equipment.deleted_at > 0) {
        continue
      }
      let equipmentUUIDStr = pbUUIDToUuid(equipment.uuid)
      let equipmentZones = equipmentZonesMap[equipmentUUIDStr]
      if (equipmentZones === undefined) {
        equipmentZones = []
      }
      let nbZones = 0
      let nbImpacts = 0

      for (let zone of equipmentZones) {
        if (zone.deleted_at > 0) {
          continue
        }
        nbZones++
        let impacts = zoneImpactsMap[pbUUIDToUuid(zone.uuid)]
        if (impacts === undefined) {
          continue
        }
        for (let impact of impacts) {
          if (impact.deleted_at > 0) {
            continue
          }
          nbImpacts++
        }
      }

      let result: IEquipmentResult | null = getEquipmentResultSM(
        equipment,
        equipmentZones,
        zoneImpactsMap,
      )
      _equipmentsProcessed.push({
        equipment: equipment,
        nb_zones: nbZones,
        nb_impacts: nbImpacts,
        result: result,
      })
    }
    _equipmentsProcessed.sort((a, b) => {
      return a.equipment.equipment_name.localeCompare(b.equipment.equipment_name)
    })
    setEquipmentsProcessed(_equipmentsProcessed)
  }, [equipments, equipmentZonesMap, zoneImpactsMap])

  const columns = useMemo((): ColumnsType<IEquipmentProcessedSM> => {
    return [
      {
        title: "", // Photo
        width: "3rem",
        render: (_, equipment) => {
          let pictureUUID = equipment.equipment.pictures[0]
          if (pictureUUID === undefined) {
            return <div style={{ width: "3rem" }} />
          }
          return (
            <TableImageThumbnail
              pictureUUID={pictureUUID}
              alt={equipment.equipment.equipment_name}
            />
          )
        },
      },
      {
        key: "equipment",
        title: <Translated keyEn="Equipment" />,
        render: (_, equipment) => {
          return equipment.equipment.equipment_name ?? "—"
        },
      },
      {
        key: "reference",
        title: <Translated keyEn="Reference" />,
        render: (_, equipment) => {
          return equipment.equipment.equipment_reference ?? "—"
        },
      },
      {
        key: "manufacturer",
        title: <Translated keyEn="Manufacturer" />,
        render: (_, equipment) => {
          return equipment.equipment.equipment_manufacturer ?? "—"
        },
      },
      {
        key: "installed_at",
        title: <Translated keyEn="Installation" />,
        render: (_, equipment) => {
          if (equipment.equipment.equipment_installation_date === 0) {
            return "—"
          }
          return formatDayOrLongDash(equipment.equipment.equipment_installation_date, true)
        },
      },
      {
        title: <Translated keyEn="#&nbsp;Zones" />,
        render: (_, equipment) => {
          if (equipmentZonesMap === null) {
            return null
          }
          let equipmentUUIDStr = pbUUIDToUuid(equipment.equipment.uuid)
          let equipmentZones = equipmentZonesMap[equipmentUUIDStr]
          let nbZones = 0
          if (!equipmentZones) {
            return <span>{nbZones}</span>
          }
          for (let zone of equipmentZones) {
            if (zone.deleted_at === 0) {
              nbZones++
            }
          }
          return <span>{nbZones}</span>
        },
      },
      {
        key: "result",
        title: (
          <Tooltip overlay={<Translated keyEn="Result" />}>
            <Translated keyEn="Result" />
          </Tooltip>
        ),
        render: (_, equipment) => {
          if (equipment.result === null) {
            return <Tag>N/A</Tag>
          }
          return <EResultTagAdequacy result={equipment.result.result} />
        },
      },
    ]
  }, [equipmentZonesMap, zoneImpactsMap])

  return (
    <Table
      rootClassName="printed-table"
      dataSource={equipmentsProcessed ?? []}
      columns={columns}
      pagination={false}
      rowKey={(e) => pbUUIDToUuid(e.equipment.uuid)}
      size="small"
      bordered
      showSorterTooltip={false}
      style={{
        width: "100%",
      }}
    />
  )
}
